import ColorThief from "colorthief";

// Cache for color extraction results
const colorCache = new Map();

// Debounce function to prevent too many simultaneous requests
const debounceMap = new Map();

const convertToCloudFrontUrl = (s3Url) => {
  if (!s3Url) return s3Url;
  return s3Url.replace(
    "https://hhcourses-assets.s3.us-east-2.amazonaws.com",
    "https://d10jmd9bhq0s4v.cloudfront.net"
  );
};

const calculateLuminance = (color) => {
  const rgb = color.match(/\d+/g);
  const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
  return luminance;
};

export const extractColorOptimized = async (imageUrl) => {
  if (!imageUrl) return null;

  const cloudFrontUrl = convertToCloudFrontUrl(imageUrl);
  
  // Check cache first
  const cached = colorCache.get(cloudFrontUrl) || localStorage.getItem(`color_${cloudFrontUrl}`);
  if (cached) {
    if (typeof cached === 'string') {
      return { dominantColor: cached, textColor: calculateLuminance(cached) > 0.5 ? "#000" : "#fff" };
    }
    return cached;
  }

  // Check if extraction is already in progress
  if (debounceMap.has(cloudFrontUrl)) {
    return debounceMap.get(cloudFrontUrl);
  }

  // Create the extraction promise
  const extractionPromise = new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(cloudFrontUrl, {
        method: "GET",
        headers: {
          "Cache-Control": "max-age=3600", // Cache for 1 hour
        },
      });

      if (!response.ok) {
        throw new Error(`Image fetch failed: ${response.status}`);
      }

      const blob = await response.blob();
      const reader = new FileReader();

      reader.onloadend = () => {
        const image = new Image();
        image.src = reader.result;

        image.onload = () => {
          try {
            const colorThief = new ColorThief();
            const dominantColorRGB = colorThief.getColor(image);
            const dominantColorCSS = `rgb(${dominantColorRGB[0]}, ${dominantColorRGB[1]}, ${dominantColorRGB[2]})`;
            const luminance = calculateLuminance(dominantColorCSS);
            const textColor = luminance > 0.5 ? "#000" : "#fff";

            const result = { dominantColor: dominantColorCSS, textColor };

            // Cache the result
            colorCache.set(cloudFrontUrl, result);
            localStorage.setItem(`color_${cloudFrontUrl}`, dominantColorCSS);

            resolve(result);
          } catch (colorError) {
            reject(colorError);
          }
        };

        image.onerror = () => {
          reject(new Error("Image failed to load"));
        };
      };

      reader.onerror = () => {
        reject(new Error("FileReader failed"));
      };

      reader.readAsDataURL(blob);
    } catch (error) {
      reject(error);
    }
  });

  // Store the promise to prevent duplicate requests
  debounceMap.set(cloudFrontUrl, extractionPromise);

  try {
    const result = await extractionPromise;
    debounceMap.delete(cloudFrontUrl);
    return result;
  } catch (error) {
    debounceMap.delete(cloudFrontUrl);
    console.error("Error extracting color:", error);
    // Return a default color on error
    return { dominantColor: "#dd6260", textColor: "#fff" };
  }
};

// Preload color extraction for critical images
export const preloadColors = (imageUrls) => {
  const promises = imageUrls.slice(0, 3).map(url => {
    // Only preload for first 3 images to avoid overwhelming the browser
    return extractColorOptimized(url).catch(() => null);
  });
  
  return Promise.allSettled(promises);
};

// Clear cache if needed (for memory management)
export const clearColorCache = () => {
  colorCache.clear();
  // Clear localStorage color cache older than 7 days
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('color_')) {
      // For simplicity, we'll just clear all color cache
      localStorage.removeItem(key);
    }
  });
}; 